import { useEffect, useMemo, useState } from "react";
import { Check, Monitor, Moon, Palette, Sun } from "lucide-react";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  useTheme,
  type ResolvedThemePreference,
} from "@/theme/ThemeContext";
import { cn } from "@/lib/utils";

// =============================================================================
// FirstTimeThemeDialog
//
// Pops on session bootstrap when the server returns
// `themePreference: "unset"` for the current user — i.e. they've
// never picked. Three options laid out as big, equally-weighted
// cards so the user understands the choice without reading copy:
//
//   Light  — bright UI, white surfaces, navy text
//   Dark   — the original look (highlighted as "what you have now")
//   System — follows the OS and re-resolves live
//
// Saving the pick:
//   • Writes the choice through ThemeProvider's `setPreference`,
//     which updates localStorage + posts to /api/account/theme +
//     applies `data-theme` to <html> on the spot.
//   • Closing the dialog without a pick is intentionally not
//     supported — there is no X / Cancel button. The user has to
//     decide; we don't want a half-state where the dialog dismisses
//     and the user's preference is still "unset" so it opens again
//     next session. Pressing Esc just collapses focus, not the
//     dialog (modal=true + onPointerDownOutside prevented).
//
// The dialog previews the cards in a way that *shows* each theme
// (a tiny mock of the page surface), so the picker reads as
// "pick the look you like" instead of "pick a setting label".
// =============================================================================

export function FirstTimeThemeDialog() {
  const { needsFirstTimePick, preference, setPreference } = useTheme();
  // Hold a local pending selection so the user can compare without
  // committing — the Save button writes it. Default to the user's
  // current OS preference: a light-mode OS user gets "Light"
  // pre-selected, a dark-mode OS user gets "Dark", and the rest
  // get "System" so nothing gets a forced default by the picker.
  const [pending, setPending] = useState<ResolvedThemePreference>(() =>
    detectOsDefault(preference),
  );

  // Re-seed `pending` whenever the dialog re-opens (e.g. an admin
  // resets the preference back to "unset" via the API and the user
  // sees the picker on next login). Without this, the previous
  // session's pending state would stick.
  useEffect(() => {
    if (needsFirstTimePick) {
      setPending(detectOsDefault(preference));
    }
  }, [needsFirstTimePick, preference]);

  const options = useMemo<
    Array<{
      value: ResolvedThemePreference;
      title: string;
      blurb: string;
      icon: JSX.Element;
    }>
  >(
    () => [
      {
        value: "light",
        title: "Light",
        blurb:
          "Bright background, deep-navy text. Easier on the eyes in well-lit dispatch rooms.",
        icon: <Sun className="h-5 w-5 text-amber-700 dark:text-amber-400" />,
      },
      {
        value: "dark",
        title: "Dark",
        blurb:
          "The original Mount Sinai look. What 100+ existing users have been on.",
        icon: <Moon className="h-5 w-5 text-foreground/80" />,
      },
      {
        value: "system",
        title: "Follow my system",
        blurb:
          "Use the OS-level theme. Re-resolves live when it flips (e.g. macOS auto-dark at sunset).",
        icon: <Monitor className="h-5 w-5 text-primary" />,
      },
    ],
    [],
  );

  if (!needsFirstTimePick) return null;

  return (
    <Dialog open>
      <DialogContent
        // Tighter max-width than the default dialog — three cards
        // stacked vertically read cleaner than a 3-column row that
        // wraps awkwardly on smaller laptops.
        className="max-w-[460px]"
        structured
        // No close button: the user has to pick (see above), and with
        // `open` fixed on, an X would do nothing when clicked.
        hideCloseButton
        // Lock the dialog: no Esc, no outside-click dismiss. We want
        // the user to make a deliberate choice and not flicker the
        // picker back open after a near-miss click.
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader icon={Palette}>
          <DialogTitle>Pick a look for the trip form</DialogTitle>
          <DialogDescription>
            We just added a light theme. Pick the one you'd like to start
            with — you can switch any time from the avatar menu in the top
            right.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="grid gap-2">
            {options.map((opt) => {
              const active = pending === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setPending(opt.value)}
                  className={cn(
                    "flex items-start gap-3 rounded-2xl border px-3 py-3 text-left transition",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    active
                      ? "border-primary bg-primary/[0.08]"
                      : "border-border bg-foreground/[0.02] hover:border-primary/45 hover:bg-foreground/[0.05]",
                  )}
                  aria-pressed={active}
                >
                  <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-foreground/[0.06]">
                    {opt.icon}
                  </span>
                  <div className="flex flex-1 flex-col gap-0.5">
                    <span className="text-sm font-bold text-foreground">
                      {opt.title}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {opt.blurb}
                    </span>
                  </div>
                  {active ? (
                    <Check className="mt-1 h-5 w-5 flex-shrink-0 text-primary" />
                  ) : (
                    <span className="mt-1 h-5 w-5 flex-shrink-0" aria-hidden="true" />
                  )}
                </button>
              );
            })}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            size="sm"
            onClick={() => setPreference(pending)}
          >
            <Check className="h-4 w-4" /> Save and continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Default the pending pick to whatever's closest to the user's
 * current world: if their OS is already light, pre-select Light; if
 * dark, pre-select Dark; otherwise (or when matchMedia isn't
 * available), default to "System" so the choice is conservative.
 *
 * Falls back to the user's currently-resolved preference if the
 * media query has no opinion — keeps the dialog from defaulting to
 * something that contradicts what's currently rendered.
 */
function detectOsDefault(
  current: ResolvedThemePreference,
): ResolvedThemePreference {
  if (typeof window === "undefined" || !window.matchMedia) return current;
  const light = window.matchMedia("(prefers-color-scheme: light)").matches;
  const dark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  if (light) return "light";
  if (dark) return "dark";
  return "system";
}
